import { useMutation, useQueryClient } from '@tanstack/react-query';

import { axiosPrivate } from 'app/utils';

import { markWebsiteAsChanged } from '../../../../hooks/usePlusWebsites';
import { broadcastPropsUpdated } from '../../../../components/preview-messages';
import type { PageDetailsResponse, Section } from './useGetPageDetails';
import { pageDetailsQueryKey } from './useGetPageDetails';

interface ReorderSectionsParams {
  sectionIds: string[];
}

function reorderSections(websiteId: string, pageId: string, { sectionIds }: ReorderSectionsParams) {
  return axiosPrivate
    .put(`/v3/pharmacist/plus-websites/${websiteId}/pages/${pageId}/sections/order`, { sectionIds })
    .then((r) => r.data);
}

export function useReorderSections({ websiteId, pageId }: { websiteId: string; pageId: string }) {
  const queryClient = useQueryClient();
  const queryKey = pageDetailsQueryKey(websiteId, pageId);

  return useMutation({
    mutationFn: (params: ReorderSectionsParams) => reorderSections(websiteId, pageId, params),
    onMutate: async ({ sectionIds }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<PageDetailsResponse>(queryKey);

      if (previous) {
        const sectionsById = new Map(previous.sections.map((section) => [section.id, section]));
        const reordered = sectionIds
          .map((id) => sectionsById.get(id))
          .filter((section): section is Section => Boolean(section))
          .map((section, index) => ({ ...section, position: index }));

        queryClient.setQueryData<PageDetailsResponse>(queryKey, {
          ...previous,
          sections: reordered,
        });
      }

      return { previous };
    },
    onError: (_error, _params, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSuccess: () => markWebsiteAsChanged(queryClient, websiteId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
      broadcastPropsUpdated();
    },
    meta: {
      errorMessage: 'Failed to reorder sections',
    },
  });
}
